import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import useForm from "../hooks/useForm";
import FileInputForm from "../component/FileInputForm";
import Validate from "../validations/index";
import applySchema from "../validations/schema/validate-applyForm";
import contactSchema from "../validations/schema/validate-contactForm";
import { sendContactForm, sendApplicationForm } from "../services/emailService";

const styles = {
  container: { 
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  button: {
    backgroundColor: "#23295b",
    fontSize: {
      xs: ".8rem",
      sm: "1rem",
      md: "1rem",
      lg: "1rem",
      xl: "1.3rem",
    },
    textTransform: "none",
  },
};

const limitRegex = /429/gi;
const isLimit = (str) =>
  str.match(limitRegex)
    ? "Too many request from this IP, please try again in 15 minutes."
    : str;

function AppForm({ view }) {
  const ref = useRef();
  const schema = ["apply", "careers"].includes(view)
  ? applySchema
  : contactSchema;

  const [formErrors, setFormErrors] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dataMessage, setDataMessage] = useState({
    state: false,
    message: null
  });
  const [dataLoading, setDataLoading] = useState(true);
  const [resetTrigger, setResetTrigger] = useState(false);

  const { formData, action, handleSubmit, handleChange, handleFile, resetForm } =
    useForm();

  useEffect(() => {
    if (action !== "onSubmit") return;

    async function formSubmit() {
      const { validData, valid, errors } = new Validate(formData, schema);
      if (!valid) {
        setFormErrors(errors);
        return;
      }

      setFormErrors(null);
      setDialogOpen(true);
      setDataLoading(true);

      try {
        let res;

        if (view === "contact") {
          res = await sendContactForm(validData);
        } else {
          const formDataObj = new FormData();
          Object.entries(validData).forEach(([key, value]) => {
            if (key !== 'file') formDataObj.append(key, value)
        });
            if (formData.file) formDataObj.append("file", formData.file);

          res = await sendApplicationForm(formDataObj);
        }

        setDataLoading(false);

        if (res.data?.success) {
          ref.current?.reset();
          resetForm();
          setDataMessage({ state: true, message: "Your email has been sent successfully!" });
          setResetTrigger((prev) => !prev);
        } else {
          setDataMessage({ state: false, message: res.data?.error || "Something went wrong." });
        }
      } catch (error) {
        setDataLoading(false);
        setDataMessage({ state: false, message: error.response?.data?.error || error.message });
      }
    }

    formSubmit();
  }, [action, formData, schema, resetForm, view, resetTrigger]);

  return (
    <Box
      component="form"
      ref={ref}
      onSubmit={handleSubmit}
      sx={styles.container}
    >
      <Grid
        container
        spacing={2}
        item
        xs={11}
        md={view === "careers" ? 10 : 6}
        justifyContent="center"
        alignItems="center"
        textAlign="center"
      >
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="firstName"
            label="First name (required)"
            onChange={handleChange}
            value={formData.firstName || ''}
            error={!!formErrors?.firstName}
            helperText={formErrors?.firstName || null}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="lastName"
            label="Last name (required)"
            onChange={handleChange}
            value={formData.lastName || ''}
            error={!!formErrors?.lastName}
            helperText={formErrors?.lastName || null}
          />
        </Grid>

        {view === "contact" && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="designation"
                label="Designation (required)"
                onChange={handleChange}
                value={formData.designation || ''}
                error={!!formErrors?.designation}
                helperText={formErrors?.designation || null}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="company"
                label="Company (required)"
                onChange={handleChange}
                value={formData.company || ''}
                error={!!formErrors?.company}
                helperText={formErrors?.company || null}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="contactNumber"
                label="Contact Number (required)"
                onChange={handleChange}
                value={formData.contactNumber || ''}
                error={!!formErrors?.contactNumber}
                helperText={formErrors?.contactNumber || null}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            name="emailAddress"
            label="Email (required)"
            onChange={handleChange}
            value={formData.emailAddress || ''}
            error={!!formErrors?.emailAddress}
            helperText={formErrors?.emailAddress || null}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="message"
            label="Message"
            variant="outlined"
            multiline
            fullWidth
            rows={5}
            onChange={handleChange}
            value={formData.message || ''}
            error={!!formErrors?.message}
            helperText={formErrors?.message || null}
          />
        </Grid>
        <Grid item xs={12}>
          {["apply", "careers"].includes(view) && (
            <FileInputForm
              onFileChange={handleFile}
              errors={formErrors?.file}
              resetTrigger={resetTrigger}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={styles.button}
          >
            {["apply", "careers"].includes(view) ? "Submit" : "Send Message"}
          </Button>
        </Grid>
      </Grid>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>
          {!dataLoading 
            ? (dataMessage.state 
            ? "Form Submitted!" 
            : "Form Error") 
            : null}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dataLoading 
              ? <CircularProgress color="inherit" /> 
              : isLimit(dataMessage.message)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {!dataLoading && 
            <Button onClick={() => setDialogOpen(false)}>
              Ok
            </Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

AppForm.propTypes = {
  view: PropTypes.string,
};

AppForm.defaultProps = {
  view: null
};

export default AppForm;
